import { Injectable } from '@angular/core';
import { DateTimeCompareEnum } from '@core/enums/date-time-compare.enum';
import {
    compareAsc,
    format,
    getDaysInMonth,
    getWeeksInMonth,
    isThisMonth,
    lastDayOfMonth,
    parse,
    startOfMonth,
    startOfYesterday,
} from 'date-fns';
import {
    DateFormatsConst,
    ISO8601RegExp,
    WithoutTISO8601RegExp,
} from '@core/constants/date-formats.const';

const moment = require('moment');

@Injectable({ providedIn: 'root' })
export class DateTimeService {
    getDate(date?: string | number | Date): Date {
        if (!date) {
            return new Date();
        }
        // due to date parsing problem in firefox, safari
        if (typeof date === 'string' && isNaN(Date.parse(date as string))) {
            const replacedDashDate = date.replace(/-/g, '/');
            return new Date(replacedDashDate);
        }
        return new Date(date);
    }

    getYear(date?: string | number | Date): string {
        if (!date) {
            return new Date().getFullYear().toString();
        }
        // due to date parsing problem in firefox, safari
        if (typeof date === 'string' && isNaN(Date.parse(date as string))) {
            const replacedDashDate = date.replace(/-/g, '/');
            return new Date(replacedDashDate).getFullYear().toString();
        }
        return new Date(date).getFullYear().toString();
    }

    getMonth(date?: string | Date): number {
        return this.getDate(date).getMonth();
    }

    getMonthDay(date?: string | Date): number {
        return this.getDate(date).getDate();
    }

    getDay(date?: string | Date): number {
        return this.getDate(date).getDay();
    }

    getTime(date?: string | Date) {
        return this.getDate(date).getTime();
    }

    getDaysInMonth(date?: string | Date): number {
        return getDaysInMonth(this.getDate(date));
    }

    startOfMonth(date?: string | Date): Date {
        return startOfMonth(this.getDate(date));
    }

    endOfMonth(date?: string | Date): Date {
        return lastDayOfMonth(this.getDate(date));
    }

    isThisMonth(date: Date): boolean {
        return isThisMonth(this.getDate(date));
    }

    isValidISODate(date?: string, withoutTISO8601 = false): boolean {
        if (!date) return false;

        // return moment(date, moment.ISO_8601).isValid();
        return withoutTISO8601
            ? WithoutTISO8601RegExp.test(date)
            : ISO8601RegExp.test(date);
    }

    startOfYesterday(): Date {
        return startOfYesterday();
    }

    getWeeksInMonth(date: Date): number {
        return getWeeksInMonth(date);
    }

    compareDates(
        date1: string | Date | number,
        date2: string | Date | number
    ): DateTimeCompareEnum {
        const leftDate = this.getDate(date1);
        const rightDate = this.getDate(date2);
        return compareAsc(leftDate, rightDate);
    }

    compareDatesWithoutTime(
        date1: string | Date | number,
        date2: string | Date | number
    ): DateTimeCompareEnum {
        const leftDate = this.getDateWithoutTime(date1);
        const rightDate = this.getDateWithoutTime(date2);

        return this.compareDates(leftDate, rightDate);
    }

    getDateWithoutTime(date: string | Date | number): Date {
        const dateWithoutTime = this.getDate(date);
        dateWithoutTime.setHours(0, 0, 0, 0);
        return dateWithoutTime;
    }

    getUTCWithoutTime(date: string | Date | number): Date {
        const newDate = this.getDate(date);
        if (newDate.getUTCHours() !== 0) {
            newDate.setUTCHours(0, 0, 0, 0);
        }
        return newDate;
    }

    parseDate(date: string, formatString: string): Date {
        return parse(date, formatString, this.getDate());
    }

    format(date: string | Date | number, dateFormat: string): string {
        if (date) {
            return format(this.getDate(date), dateFormat);
        }
        return '';
    }

    filterDates(dates: string[]): (date: Date | null) => boolean {
        return (date: Date | null): boolean => {
            if (!date) {
                return false;
            }
            const isDate = dates.find((eventDate) => {
                const withoutTimeDate = this.getUTCWithoutTime(date);
                return (
                    this.compareDatesWithoutTime(eventDate, withoutTimeDate) ===
                    DateTimeCompareEnum.Equal
                );
            });
            return Boolean(isDate);
        };
    }

    fromMomentToDate(momentDate: any): Date {
        return moment(momentDate).toDate();
    }

    getDateFromISOString(value: string, withoutTISO8601 = false): string {
        if (!value) {
            return '';
        }

        if (!this.isValidISODate(value, withoutTISO8601)) {
            return 'Unsupported Date';
        }

        return this.defaultFormat(value);
    }

    defaultFormat(date: string | Date | number, isUtc: boolean = true): string {
        // P.Bruyaka: Try-catch to prevent any exception and Invalid Time which was thrown when column is string with value like '1231233' which is parsed to date by moment and is valid date
        try {
            if (date) {
                return format(
                    this.splitDateTime(date),
                    this.compareDates(
                        date,
                        isUtc
                            ? this.getUTCWithoutTime(date)
                            : this.getDateWithoutTime(date)
                    ) === DateTimeCompareEnum.Equal
                        ? DateFormatsConst.FULL_DATE_FULL_YEAR
                        : DateFormatsConst.COMMON_DATEFORMAT
                );
            }
            return '';
        } catch (err) {
            return date?.toString();
        }
    }

    splitDateTime(date: string | number | Date): number | Date {
        if (!date) {
            return 0;
        }

        let splitDate;
        if (date.toString().includes('T')) {
            splitDate = date.toString().split('T');
        } else {
            splitDate = date.toString().split(' ');
        }

        const splitDateElement = splitDate[0];
        const splitTimeElement = splitDate[1];

        if (!splitTimeElement) {
            return this.getDate(date);
        }

        const parsedTime = this.parseTime(splitTimeElement);

        return parsedTime.isExist
            ? this.getDate(date)
            : this.parseDate(
                  splitDateElement,
                  DateFormatsConst.ISO_FULL_DATE_FULL_YEAR
              );
    }

    parseTime(time: string): {
        isExist: boolean;
        h: number;
        m: number;
        s: number;
    } {
        // Format 00:00:00.000Z
        if (!time) {
            return {
                isExist: false,
                h: 0,
                m: 0,
                s: 0,
            };
        }

        const splitTime = time.split(':');
        const hours = Number(splitTime[0]),
            minutes = Number(splitTime[1]),
            seconds = Number(splitTime[2]?.split('.')[0]?.split('Z')[0]);

        const isExist = Boolean(
            (splitTime.length && hours) || minutes || seconds
        );

        return {
            isExist,
            h: hours,
            m: minutes,
            s: seconds,
        };
    }
}
